import { Injectable } from '@angular/core';
import { forkJoin, Observable, of } from 'rxjs';
import { map, switchMap, take } from 'rxjs/operators';
import { StringAnyMap } from 'src/app/core/models/common.models';
import { IdeasSortFiltersService } from 'src/app/core/services/ideas-sort-filters.service';
import { CategoriesStoreService } from 'src/app/core/store/services/categories-store.service';
import { FiltersStoreService } from 'src/app/core/store/services/filters-store.service';
import { IdeasStoreService } from 'src/app/core/store/services/ideas-store.service';
import { TagsStoreService } from 'src/app/core/store/services/tags-store.service';
import { TerminologyStoreService } from 'src/app/core/store/services/terminology-store.service';

@Injectable({
  providedIn: 'root',
})
export class ProductDataService {
  constructor(
    private tagsStore: TagsStoreService,
    private ideasStore: IdeasStoreService,
    private categoriesStore: CategoriesStoreService,
    private filtersStoreService: FiltersStoreService,
    private termStoreService: TerminologyStoreService,
    private ideasFiltersService: IdeasSortFiltersService,
  ) {}

  public loadProductRelatedData(productId: string, params?: StringAnyMap): Observable<boolean[]> {
    return forkJoin([
      this.loadTags(productId), //
      this.loadTerminology(productId),
      this.loadIdeas(productId, params),
      this.loadCategories(productId, params),
    ]);
  }

  public loadIdeas(productId: string, params?: StringAnyMap): Observable<boolean> {
    if (params?.categoriesList) {
      this.filtersStoreService.reset();
      return of(true);
    }

    return this.ideasFiltersService.routeParamsToIdeasReq({ ...params }).pipe(
      take(1),
      switchMap((req) => {
        return this.ideasStore.load({ ...req, page: params?.page, productId });
      }),
      map(() => true),
    );
  }

  public loadTags(productId: string): Observable<boolean> {
    return this.tagsStore.load({ productId }).pipe(map(() => true));
  }

  public loadCategories(productId: string, params?: StringAnyMap): Observable<boolean> {
    const categoryId: string | undefined = params?.categoryId;

    return this.categoriesStore.loadList(productId).pipe(
      map((res) => {
        if (!categoryId) {
          this.categoriesStore.selectById(void 0);
          return true;
        }

        if (!res.categories[categoryId]) {
          return false;
        }

        this.categoriesStore.selectById(categoryId);
        return true;
      }),
    );
  }

  public loadTerminology(productId: string): Observable<boolean> {
    return this.termStoreService.loadWorkspaceTerms(productId).pipe(map(() => true));
  }
}
